import React, { useCallback, useEffect, useState } from 'react';
import * as S from '../Partners/styles';
import axios from 'axios';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { Link } from "react-router-dom";
import { getTableRowClass, getAnalysisStatusSiglum, getAnalysisStatusColor, getAnalysisStatus } from "../../utils/utils"
import { Tooltip } from '@mui/material';
import { Check, ErrorOutline } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
    tableStyle: {
      border: "none",
      boxShadow: "none",
    },
    tableText: {
      fontFamily: "Open Sans",
      fontWeight: 'normal',
      fontSize: "12px",
      lineHeight: "24px",
      color: "#333",
      textAlign: "center",
      margin: "10px",
      padding: "6px",
    },
    tableHeader: {
      textAlign: "center",
      fontFamily: "Open Sans",
      fontStyle: "normal",
      fontWeight: "normal",
      color: '#6F6F6F',
      fontSize: "14px",
      lineHeight: "19px",
      padding: "6px",
    }
  }));

export default function LegalRepresentativesTable({ partners }) {

    const classes = useStyles();
    useTheme();

    const [legalRepresentatives, setLegalRepresentatives] = useState([])

    const filterLegalRepresentatives = useCallback(() => {
        return (partners || []).filter((partner) => partner.is_payload_legal_representative)
    }, [partners])

    useEffect(() => {
        setLegalRepresentatives(filterLegalRepresentatives());
    }, [filterLegalRepresentatives])

    return (
        <S.Container>
            <S.Title>Quadro de Representantes Legais Informados</S.Title>
            {legalRepresentatives.length > 0 ?
                <Paper className={classes.tableStyle}>
                    <Table style={{ padding: "8px" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeader}>Nome</TableCell>
                                <TableCell className={classes.tableHeader}>CPF</TableCell>
                                <TableCell className={classes.tableHeader}>PEP/Relacionado</TableCell>
                                <TableCell className={classes.tableHeader}>Também é sócio</TableCell>
                                <TableCell className={classes.tableHeader}>Status da Análise</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {legalRepresentatives.map((legalRepresentative, rowIndex) => (
                                <LegalRepresentativeRow
                                    key={legalRepresentative.document_number}
                                    legalRepresentative={legalRepresentative}
                                    rowIndex={rowIndex}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
                :
                <div style={{color: '#6f6f6f', width: '100%', textAlign: 'center', padding: '16px 0'}}>Informação indisponível</div>
            }
        </S.Container>
    );
}

function retrieveLatestValidAnalysisStatusEvent(analysisStatusEvents) {
    const decidedStatuses = ['in_manual_analysis', 'automatically_approved', 'automatically_reproved'];
    const events = analysisStatusEvents || [];
    const decidedEvents = events.filter((event) => event.decision_metadata && decidedStatuses.includes(event.new_status));

    if (decidedEvents.length > 0) {
        return decidedEvents[decidedEvents.length - 1];
    }

    return events[events.length - 1];
}

function PartnershipBadge({ isBureauPartner, isPayloadPartner }) {
    if (isBureauPartner && isPayloadPartner) {
        return (
            <Tooltip title="Representante também é sócio, validado na base de dados" placement='left'>
                <S.GreenBadge>Sim</S.GreenBadge>
            </Tooltip>
        )
    }

    if (isBureauPartner && !isPayloadPartner) {
        return (
            <Tooltip title="Representante também é sócio encontrado na base de dados, não fornecido" placement='left'>
                <S.GreenBadge>Sim</S.GreenBadge>
            </Tooltip>
        )
    }

    if (!isBureauPartner && isPayloadPartner) {
        return (
            <Tooltip title="Representante também é sócio fornecido, não encontrado na base de dados" placement='left'>
                <S.GreenBadge>Sim</S.GreenBadge>
            </Tooltip>
        )
    }

    return (<>Não</>)
}

function PepBadge({ pep, relatedPep }) {
    if (pep?.flag === 'negative') {
        return (
            <Tooltip title={pep.description} placement='right'>
                <S.RedBadge>Sim</S.RedBadge>
            </Tooltip>
        )
    }

    if (relatedPep?.flag === 'negative') {
        return (
            <Tooltip title={relatedPep.description} placement='right'>
                <S.RedBadge>Sim</S.RedBadge>
            </Tooltip>
        )
    }

    if (pep?.flag === 'positive') {
        return (
            <Tooltip title={pep.description} placement='right'>
                <S.GreenBadge>Não</S.GreenBadge>
            </Tooltip>
        )
    }

    return (<>-</>)
}

function LegalRepresentativeRow({ legalRepresentative, rowIndex }) {

    const classes = useStyles();
    useTheme();

    const [naturalPerson, setNaturalPerson] = useState();
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const retrieveNaturalPerson = useCallback(async () => {
        if (!legalRepresentative.natural_person_key) {
            setNaturalPerson(legalRepresentative);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const response = await axios.get(`/dash/onboarding/natural_person/${legalRepresentative.natural_person_key}`)
        if (response?.status === 200) {
            setNaturalPerson(response.data);
            setIsLoading(false);
        } else {
            setHasError(true);
            setIsLoading(false);
        }
    }, [legalRepresentative])

    useEffect(() => { retrieveNaturalPerson() }, [retrieveNaturalPerson]);

    if (hasError || isLoading || !naturalPerson) {
        return <></>;
    }

    const parsedLegalRepresentative = parseLegalRepresentative(
        naturalPerson,
        legalRepresentative.is_bureau_partner,
        legalRepresentative.is_payload_partner
    );

    return (
        <TableRow className={getTableRowClass(rowIndex)}>
            <TableCell className={classes.tableText}>
                {parsedLegalRepresentative.naturalPersonKey !== undefined ?
                    <Tooltip title={`${parsedLegalRepresentative.hasNegativeFlags ? 'Há' : 'Não há'} apontamentos para essa análise de representante`} placement="right">
                        <div style={{display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center', color: "#0b1f82", width: 'fit-content'}}>
                            {parsedLegalRepresentative.hasNegativeFlags ?
                                <ErrorOutline style={{color: '#D91A1A', width: "16px", height: "16px", margin: 0}} /> :
                                <Check style={{color: '#73c580', width: "16px", height: "16px", margin: 0}} />
                            }
                            <Link className="link" to={"/natural-person/" + parsedLegalRepresentative.naturalPersonKey}>
                                <p style={{margin: 0}}>{parsedLegalRepresentative.name}</p>
                            </Link>
                        </div>
                    </Tooltip> : <p style={{margin: 0}}>{parsedLegalRepresentative.name}</p>
                }
            </TableCell>
            <TableCell className={classes.tableText}>{parsedLegalRepresentative.documentNumber}</TableCell>
            <TableCell className={classes.tableText}>
                <PepBadge pep={parsedLegalRepresentative.pep} relatedPep={parsedLegalRepresentative.relatedPep} />
            </TableCell>
            <TableCell className={classes.tableText}>
                <PartnershipBadge
                    isBureauPartner={parsedLegalRepresentative.isBureauPartner}
                    isPayloadPartner={parsedLegalRepresentative.isPayloadPartner}
                />
            </TableCell>
            <TableCell className={classes.tableText}>
                <Tooltip placement="right" title={getAnalysisStatus(parsedLegalRepresentative.analysisStatus)}>
                    <div className={["circle", "small"].join(" ")} style={{backgroundColor: getAnalysisStatusColor(parsedLegalRepresentative.analysisStatus)}}>
                        {getAnalysisStatusSiglum(parsedLegalRepresentative.analysisStatus)}
                    </div>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
}

function parseLegalRepresentative(naturalPerson, isBureauPartner, isPayloadPartner) {

    if (!naturalPerson.natural_person_key) {
        return {
            documentNumber: naturalPerson.document_number,
            name: naturalPerson.name,
            analysisStatus: "not_analised",
            isBureauPartner: isBureauPartner,
            isPayloadPartner: isPayloadPartner
        }
    }

    const latestEvent = retrieveLatestValidAnalysisStatusEvent(naturalPerson.analysis_status_events);
    const decisionMetadata = latestEvent?.decision_metadata;

    return {
        naturalPersonKey: naturalPerson.natural_person_key,
        analysisStatus: naturalPerson.analysis_status,
        documentNumber: naturalPerson.document_number,
        name: decisionMetadata?.registration_data?.name?.description ?? (naturalPerson.name ?? '-'),
        pep: decisionMetadata?.pep,
        relatedPep: decisionMetadata?.related_pep,
        hasNegativeFlags: hasNegativeFlags(decisionMetadata),
        isBureauPartner: isBureauPartner,
        isPayloadPartner: isPayloadPartner
    };
}

function hasNegativeFlags(metadata) {
    if (typeof metadata !== 'object' || metadata === null) {
        return false;
    }

    return Object.keys(metadata).some((key) => {
        if (key === 'flag' && metadata[key] === 'negative') {
            return true;
        }
        return typeof metadata[key] === 'object' && hasNegativeFlags(metadata[key]);
    });
}
